<template>
  <div id="main-wrapper">
    <NavbarDarkMini />

    <div class="formulaire-rdv">
      <div class="container">

        <!-- Chargement initial -->
        <div v-if="isLoadingInitialData" class="loading-container text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Chargement...</span>
          </div>
          <p class="mt-3">Chargement des informations...</p>
        </div>

        <!-- Erreur de chargement initial -->
        <div v-else-if="initialLoadError" class="error-container">
          <div class="alert alert-danger">
            <h4>Erreur</h4>
            <p>{{ initialLoadError }}</p>
            <button @click="$router.go(-1)" class="btn btn-primary mt-3">
              Retour
            </button>
          </div>
        </div>

        <!-- Confirmation d'envoi -->
        <div v-else-if="sollicitationsStore.sollicitationEnvoyee" class="confirmation-container">
          <div class="alert alert-success">
            <h4><i class="fas fa-check-circle"></i> Sollicitation envoyée avec succès !</h4>
            <p>L'avocat vous contactera prochainement.</p>

            <a href="https://www.callalawyer.fr">
              Retour à Call a Lawyer
            </a>
          </div>
        </div>

        <!-- Formulaire -->
        <div v-else class="rdv-content">
          <div class="row justify-content-center">
            <div class="rdv-main-content col-lg-8">
              <div class="step-container">
                <h2>Solliciter un avocat</h2>
                <p class="step-description">Remplissez ce formulaire pour contacter l'avocat</p>

                <div class="step-content">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="prenom">Prénom *</label>
                        <input id="prenom" v-model="clientForm.prenom" type="text" class="form-control" required />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="nom">Nom *</label>
                        <input id="nom" v-model="clientForm.nom" type="text" class="form-control" required />
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="email">Email *</label>
                        <input id="email" v-model="clientForm.email" type="email" class="form-control" required />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="telephone">Téléphone *</label>
                        <input id="telephone" v-model="clientForm.telephone" type="tel" class="form-control" required />
                      </div>
                    </div>
                  </div>

                  <div class="form-check">
                    <input id="rgpd" v-model="rgpdAccepted" type="checkbox" class="form-check-input" required />
                    <label for="rgpd" class="form-check-label">
                      J'accepte les <a href="https://www.callalawyer.fr/legal/informations-utilisateurs"
                        target="_blank">CGU</a> *
                    </label>
                  </div>

                  <div class="form-check">
                    <input id="politique-de-confidentialite" v-model="politiqueAccepted" type="checkbox"
                      class="form-check-input" required />
                    <label for="politique-de-confidentialite" class="form-check-label">
                      J'accepte que mes données personnelles soient collectées et traitées conformément à <a
                        href="https://www.callalawyer.fr/legal/politique-de-confidentialite" target="_blank">la
                        politique de confidentialité</a> *
                    </label>
                  </div>

                  <!-- Message d'erreur -->
                  <div v-if="sollicitationsStore.error" class="alert alert-danger mt-3">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ sollicitationsStore.error }}
                  </div>
                </div>

                <!-- Bouton d'envoi -->
                <div class="step-navigation">
                  <div class="navigation-left"></div>
                  <div class="navigation-right">
                    <button @click="envoyerSollicitation" :disabled="!formulaireValide || sollicitationsStore.loading"
                      class="btn btn_form btn-success">
                      <span v-if="sollicitationsStore.loading" class="spinner-border spinner-border-sm mr-2"></span>
                      {{ sollicitationsStore.loading ? 'Envoi...' : 'Envoyer la sollicitation' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sidebar -->
            <div class="rdv-sidebar-content col-lg-4">
              <div class="sidebar">
                <div class="sidebar-header" v-if="sollicitationsStore.avocatCible">
                  <div class="sidebar-img">
                    <img v-if="sollicitationsStore.avocatCible.photo" :src="sollicitationsStore.avocatCible.photo"
                      :alt="`Photo de ${sollicitationsStore.avocatCible.prenom} ${sollicitationsStore.avocatCible.nom}`">
                    <img v-else src="@/assets/img/balance.png" alt="Photo par défaut">
                  </div>
                  <div class="sidebar-content">
                    <div class="rdv-target">
                      <div class="target-info">
                        <span>{{ sollicitationsStore.avocatCible.prenom }} {{ sollicitationsStore.avocatCible.nom
                        }}</span>
                      </div>
                    </div>
                    <div v-if="sollicitationsStore.avocatCible.adresse" class="rdv-place">
                      <span class="rdv-place-street">
                        {{ sollicitationsStore.avocatCible.adresse.rue }}
                      </span>
                      <span class="rdv-place-city">
                        {{ sollicitationsStore.avocatCible.adresse.codePostal }} {{
                          sollicitationsStore.avocatCible.adresse.ville }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FooterDarkMini />

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSollicitationsStore } from '@/stores'
import { AvocatsApi, Configuration } from '@/api'
import NavbarDarkMini from '@/components/navbar/navbar-dark-mini.vue'
import FooterDarkMini from '@/components/footer/footer-dark-mini.vue'

// Configuration de l'API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
})

export default {
  name: 'SollicitationAvocat',
  components: {
    NavbarDarkMini,
    FooterDarkMini
  },
  props: {
    prescriber: {
      type: String,
      default: 'call-a-lawyer'
    },
    slugAvocat: {
      type: String,
      default: undefined
    }
  },
  setup(props) {
    const route = useRoute()
    const sollicitationsStore = useSollicitationsStore()

    // État de chargement initial
    const isLoadingInitialData = ref(false)
    const initialLoadError = ref(null)
    const rgpdAccepted = ref(false)
    const politiqueAccepted = ref(false)

    // Formulaire client local (synchronisé avec le store)
    const clientForm = ref({
      prenom: '',
      nom: '',
      email: '',
      telephone: ''
    })

    // Validation du formulaire (locale, sans passer par le store)
    const formulaireValide = computed(() => {
      return (
        clientForm.value.prenom.trim().length > 0 &&
        clientForm.value.nom.trim().length > 0 &&
        clientForm.value.email.trim().length > 0 &&
        clientForm.value.telephone.trim().length > 0 &&
        rgpdAccepted.value &&
        politiqueAccepted.value &&
        sollicitationsStore.avocatCible !== undefined
      )
    })

    // Mettre à jour le client dans le store quand le formulaire local change
    const updateClient = () => {
      sollicitationsStore.updateClient(clientForm.value)
    }

    // Charger l'avocat depuis le slug
    const loadAvocat = async (slug) => {
      isLoadingInitialData.value = true
      initialLoadError.value = null

      try {
        const api = new AvocatsApi(apiConfig)
        const response = await api.avocatsControllerGetAvocat('slug', slug)
        sollicitationsStore.initNouveauFormulaire(response.data)
      } catch (error) {
        console.error('Erreur lors du chargement de l\'avocat:', error)
        initialLoadError.value = 'Avocat non trouvé ou erreur de chargement'
      } finally {
        isLoadingInitialData.value = false
      }
    }

    // Envoyer la sollicitation
    const envoyerSollicitation = async () => {
      if (!formulaireValide.value) return

      // Mettre à jour les données du client dans le store avant l'envoi
      updateClient()

      try {
        await sollicitationsStore.envoyerSollicitation()
        // L'erreur est gérée automatiquement dans le store
      } catch (error) {
        // L'erreur est affichée via sollicitationsStore.error
      }
    }

    // Lifecycle
    onMounted(async () => {
      // Définir le prescriber dans le store
      sollicitationsStore.prescriber = props.prescriber

      // Charger l'avocat depuis le slug (props ou route params)
      const slugAvocat = props.slugAvocat || route.params.slugAvocat
      if (slugAvocat) {
        await loadAvocat(slugAvocat)
      } else {
        initialLoadError.value = 'Aucun avocat spécifié'
      }
    })

    return {
      // Stores
      sollicitationsStore,

      // State local
      isLoadingInitialData,
      initialLoadError,
      clientForm,
      rgpdAccepted,
      politiqueAccepted,

      // Computed
      formulaireValide,

      // Methods
      envoyerSollicitation
    }
  }
}
</script>

<style lang="scss" scoped>
.formulaire-rdv {
  padding: 3rem 0 2rem;

  @media (max-width: 767px) {
    padding: 1rem 0 1rem;
    margin-block-end: 3rem;
  }
}

.loading-container,
.error-container,
.confirmation-container {
  padding: 2rem;
  text-align: center;

  .alert {
    display: inline-block;
    text-align: left;
    max-width: 600px;
    margin: 0 auto;

    h4 {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
}

.rdv-content {
  padding: 0;

  & .rdv-main-content {
    order: 1;
  }

  & .rdv-sidebar-content {
    order: 2;
  }

  @media (max-width: 767px) {
    & .rdv-main-content {
      order: 2;
    }

    & .rdv-sidebar-content {
      order: 1;

      & .sidebar-header {
        padding: 12px 12.4px;
      }
    }
  }
}

.step-container {
  padding: 1.8rem;
  border-radius: 8px;
  min-height: 400px;
  border: 1px dashed #d5dee6;
  background-color: #ffffff;
  box-shadow: 1px 1px 20px #0000000d;

  & h2 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
    font-size: 1.55rem;
    font-weight: 600;
  }

  & .step-description {
    color: #6c757d;
    margin-bottom: 2rem;
    font-size: 1rem;
    line-height: 22px;
  }
}

.step-content {
  .form-group {
    margin-bottom: 1.5rem;

    label {
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.5rem;
    }

    .form-control {
      padding: 0.75rem;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:focus {
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        outline: none;
      }
    }
  }

  .form-check {
    margin-top: 2rem;
    padding-left: 1.5rem;

    .form-check-input {
      margin-top: 0.3rem;
    }

    .form-check-label {
      color: #495057;
      font-size: 0.95rem;
    }
  }

  .alert {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.95rem;

    i {
      font-size: 1.2rem;
    }
  }
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 0;

  & .navigation-right,
  & .navigation-left {
    &>button {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 200px;
      gap: 0.5rem;
      color: #fff;
      text-align: center;
      letter-spacing: 0.03em;
      background-image: linear-gradient(to bottom, #2190ff, #005bb5);
      border-radius: 8px;
      border: none;
      padding: 11.5px 16px;
      font-size: 15px;
      font-weight: 600;
      line-height: 1.5;
      text-decoration: none;
      transition: transform 0.2s cubic-bezier(0.645, 0.045, 0.355, 1),
        background-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

      &:is(:hover, :focus):not(:disabled) {
        background-image: linear-gradient(#005bb5, #005bb5);
        transform: translate(0, 2px);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

/* Sidebar */
.sidebar {
  & .sidebar-header {
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: #fff;
    border: 1px dashed #d5dee6;
    border-radius: 8px;
    padding: 18px 18.4px;
    box-shadow: 1px 1px 20px #0000000d;

    & .sidebar-img {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      padding: 5px;
      border: 1px solid #e9ecef;
      margin: 0 auto;

      & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }

    & .sidebar-content {
      text-align: center;

      & .rdv-target {
        margin-bottom: 1rem;

        & .target-info {
          font-size: 1.25rem;
          font-weight: bold;
          color: #1a1a1a;
        }
      }

      & .rdv-place {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        color: #6c757d;
        font-size: 0.9rem;

        & .rdv-place-street {
          font-weight: 500;
        }
      }
    }
  }
}
</style>